// SOC 2 Evidence-Engine admin API client (item 65 of
// PRODUCT-ROADMAP.md, item 74 in the recommended delivery order,
// Phase A).
//
// Backs the AdminComplianceSOC2 page. The /generate endpoint streams
// a ZIP back, so we don't go through SWR for it — the UI uses a
// direct fetch + Blob → object-URL → download click pattern so the
// auditor's bundle file lands on disk with the right Content-
// Disposition filename.

import useSWR from 'swr';

export interface SOC2Criterion {
  id: string;
  title: string;
  description: string;
}

export interface SOC2Section {
  criterion: string;
  title: string;
  narrative: string;
  evidence: Record<string, unknown>;
  items: number;
}

export interface SOC2Report {
  generated_at: string;
  generated_by?: string;
  scope: { start: string; end: string };
  sections: SOC2Section[];
}

export interface SOC2Run {
  id: string;
  generated_at: string;
  generated_by?: string;
  scope_start: string;
  scope_end: string;
  criteria: string[];
  item_count: number;
  bundle_size: number;
  bundle_sha256?: string;
  note?: string;
}

const json = (url: string) =>
  fetch(url, { credentials: 'include' }).then((r) => {
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.json();
  });

export function useSOC2Criteria() {
  return useSWR<{ criteria: SOC2Criterion[] }>(
    '/api/admin/compliance/soc2/criteria',
    json,
    { revalidateOnFocus: false },
  );
}

export function useSOC2Runs() {
  return useSWR<{ runs: SOC2Run[] }>('/api/admin/compliance/soc2/runs', json, {
    refreshInterval: 30_000,
  });
}

interface PreviewInput {
  start?: string;        // YYYY-MM-DD
  end?: string;          // YYYY-MM-DD
  criteria?: string[];   // subset; empty = all
}

export async function previewSOC2(input: PreviewInput): Promise<SOC2Report> {
  const usp = new URLSearchParams();
  if (input.start) usp.set('start', input.start);
  if (input.end) usp.set('end', input.end);
  if (input.criteria && input.criteria.length > 0) {
    usp.set('criteria', input.criteria.join(','));
  }
  const url = `/api/admin/compliance/soc2/preview${usp.toString() ? '?' + usp.toString() : ''}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`${res.status}: ${body}`);
  }
  return (await res.json()) as SOC2Report;
}

interface GenerateInput extends PreviewInput {
  note?: string;
}

export interface GenerateResult {
  filename: string;
  size: number;
  sha256: string;
  itemCount: number;
}

// generateSOC2Bundle posts the request, takes the ZIP body, and
// triggers a browser download. Returns the bundle metadata derived
// from the response headers so the page can show "downloaded; sha256
// = …" without re-reading the bytes.
export async function generateSOC2Bundle(input: GenerateInput): Promise<GenerateResult> {
  const res = await fetch('/api/admin/compliance/soc2/generate', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`${res.status}: ${body}`);
  }
  const sha = res.headers.get('X-Orbital-SOC2-Bundle-SHA256') ?? '';
  const itemHeader = res.headers.get('X-Orbital-SOC2-Bundle-Items') ?? '0';
  const itemCount = Number.parseInt(itemHeader, 10) || 0;
  const cd = res.headers.get('Content-Disposition') ?? '';
  const m = /filename="([^"]+)"/.exec(cd);
  const filename = m ? m[1] : `orbitalreg-soc2-evidence-${Date.now()}.zip`;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { filename, size: blob.size, sha256: sha, itemCount };
}
